import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { User } from "@supabase/supabase-js";

export interface UserProfile {
  id: string;
  email: string;
  role: string;
  display_name: string;
  onboarding_completed: boolean;
}

export function useProfile() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const currentUserIdRef = useRef<string | null>(null);

  const fetchProfileForUser = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching user profile:", error);
      }

      setProfile((data as UserProfile | null) ?? null);
    } catch (err) {
      console.error("useProfile error:", err);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadProfile() {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          if (mounted) {
            setUser(null);
            setProfile(null);
            setLoading(false);
            currentUserIdRef.current = null;
          }
          return;
        }

        if (mounted) {
          setUser(session.user);
          currentUserIdRef.current = session.user.id;
          setLoading(true);
        }

        if (mounted) {
          await fetchProfileForUser(session.user.id);
        }
      } catch (err) {
        console.error("useProfile error:", err);
        if (mounted) {
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      }
    }

    loadProfile();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        if (mounted) {
          setUser(session.user);
          currentUserIdRef.current = session.user.id;
          setLoading(true);
        }

        void fetchProfileForUser(session.user.id);
      } else {
        if (mounted) {
          setUser(null);
          setProfile(null);
          setLoading(false);
          currentUserIdRef.current = null;
        }
      }
    });

    // Re-fetch profile when window regains focus (catches role changes made externally)
    const handleFocus = () => {
      const uid = currentUserIdRef.current;
      if (uid) {
        void fetchProfileForUser(uid);
      }
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchProfileForUser]);

  return { user, profile, loading };
}